import React, { useCallback } from 'react';
import { css } from '@emotion/react';
import moment from 'moment';

import { useSelector } from 'react-redux';
import {
  getSessionAvailabilityByDay,
  hasSessionAvailabilityLoadedForParams,
} from 'redux/selectors/mentorship-program-sessions';

import useSessionAvailabilityParams from 'athena/hooks/use-session-availability-params';
import { useAvailabilityCalendarContext } from './availability-calendar-context';

import LoadingTimeSlot from './loading-time-slot';
import EmptyTimeSlot from './empty-time-slot';
import TimeSlot from './time-slot';

import { TIME_SLOT_COLUMN_HEIGHT } from './availability-calendar-grid';

const styles = css`
  min-height: ${TIME_SLOT_COLUMN_HEIGHT}px;
`;

type TimeSlotColumnProps = {
  day: string;
};

const TimeSlotColumn = React.memo((props: TimeSlotColumnProps) => {
  const { day } = props;
  const {
    connectionId,
    mentorshipProgramId,
    duration,
  } = useSessionAvailabilityParams();

  const { endDate, getRange } = useAvailabilityCalendarContext();

  const availability = useSelector(
    getSessionAvailabilityByDay(connectionId, mentorshipProgramId, duration, day),
  );

  const isDayInPast = moment().isAfter(day, 'day');
  const isAfterProgramEndDate = endDate ? moment(day).isAfter(moment(endDate), 'day') : false;
  const { slots = [], loading } = availability;
  const isEmpty = !slots || slots.length === 0;

  const hasBeenLoaded = useSelector(hasSessionAvailabilityLoadedForParams({
    mentorshipProgramConnectionId: connectionId,
    mentorshipProgramId,
    meetingDays: getRange(),
    meetingDuration: duration,
  }));
  const isLoading = !hasBeenLoaded && loading;

  const getContent = useCallback(() => {
    if (isDayInPast || isAfterProgramEndDate) {
      return <EmptyTimeSlot />;
    }

    if (!isLoading) {
      return isEmpty ? (
        <EmptyTimeSlot />
      ) : (
        slots.map((slot) => (
          <TimeSlot key={slot.startDate} startDate={slot.startDate} />
        ))
      );
    }

    return <LoadingTimeSlot />;
  }, [isDayInPast, isAfterProgramEndDate, isLoading, isEmpty, slots]);

  return (
    <div className='w-100 time-slot' css={styles}>
      {getContent()}
    </div>
  );
});

export default TimeSlotColumn;
